import { Adaptor } from "./adaptors/adaptor.base";
import { formatPrice, PriceFormat } from "./format-price";
import type { Product, UnboundProductData, UnboundProductOptionData } from "./products/product.base";
import { Provider, ProviderData } from "./providers/provider.base";
import { InitSelectorProvider } from "./providers/provider.init-selector";
import { VlaicuProvider } from "./providers/provider.vlaicu";
import { STORE_ADAPTER, STORE_CAMPAIGN, STORE_FORMATTER, STORE_LOCALE, STORE_OVERRIDES, STORE_TRANSFORMERS, STORE_TRIAL_LINKS } from "./store.constants";

export {
  STORE_ADAPTER,
  STORE_CAMPAIGN,
  STORE_FORMATTER,
  STORE_LOCALE,
  STORE_OVERRIDES,
  STORE_TRANSFORMERS,
  STORE_TRIAL_LINKS
};

export type ProductSelector = {
  id       : string,
  campaign?: string
}

export type Locale =  `${string}-${string}`

type ProviderCtor = new (param: ProviderData) => Provider;

const BuiltInProviders = {
  init  : InitSelectorProvider,
  vlaicu: VlaicuProvider
} as const;

type ProviderSelector = {
  name: keyof typeof BuiltInProviders | ProviderCtor
};

type StoreConfig = {
  provider      : ProviderSelector,
  locale        : Locale,
  campaign     ?: (param: ProductSelector) => Promise<string | undefined>,
  trialLinks   ?: {
    [key: string]: { //id product
      default?: { //campaign
        [key: `${string}-${string}`]: { //option
          [key: string]: string
        }
      },
      [key: string]: { //campaign
        [key: `${string}-${string}`]: { //option
          [key: string]: string
        }
      } | undefined
    }
  },
  overrides    ?: {
    [key: string]: { //id in page
      default?: { //campaign in page
        campaign?: string;
        options?  : {
          [key: `${string}-${string}`]: Partial<UnboundProductOptionData> | undefined | null
        }
      },
      [key: string]: {
        campaign?: string;
        options?  : {
          [key: `${string}-${string}`]: Partial<UnboundProductOptionData> | undefined | null
        }
      } | undefined
    }
  },
  transformers ?: {
    buyLink?  : (param: { buyLink: string, product: UnboundProductData, option: UnboundProductOptionData }) => Promise<string>,
    trialLink?: (param: { name: string, trialLink: string, buyLink: string, product: UnboundProductData, option: UnboundProductOptionData }) => Promise<string>
  },
  formatter    ?: (opt: PriceFormat) => string | number
}

type CacheData = {
  provider: Provider
};

class Cache {
  private products: Map<string, Promise<Product | undefined> >;
  private provider: Provider;

  constructor(param: CacheData) {
    this.products = new Map();
    this.provider = param.provider;
  }

  async get({ id, campaign }: ProductSelector): Promise<Product | undefined> {
    const productKey = `${id}-${campaign}`;

    // Check if we already have a cached entry.
    const cacheEntry = this.products.get(productKey);
    if (cacheEntry) {
      return cacheEntry;
    }

    // If no cache entry exists, call the provider and cache the result.
    const productPromise = this.provider.getProduct({ id, campaign });
    this.products.set(productKey, productPromise);
    return await productPromise;
  }
}

export class Store  {
  private cache               : Cache;
  private [STORE_LOCALE]      : Locale;
  private [STORE_ADAPTER]     : Promise<Adaptor>;
  private [STORE_CAMPAIGN]    : NonNullable<StoreConfig["campaign"]>;
  private [STORE_TRANSFORMERS]: StoreConfig["transformers"];
  private [STORE_OVERRIDES]   : StoreConfig["overrides"];
  private [STORE_TRIAL_LINKS] : StoreConfig["trialLinks"];
  private [STORE_FORMATTER]   : NonNullable<StoreConfig["formatter"]>;

  constructor(config: StoreConfig) {
    const defaultCampaign = async ({ campaign }: ProductSelector) => campaign;
    this[STORE_LOCALE] = config.locale;
    this[STORE_ADAPTER] = Adaptor.create();
    this[STORE_CAMPAIGN] = config.campaign ?? defaultCampaign;
    this[STORE_TRANSFORMERS] = config.transformers;
    this[STORE_OVERRIDES] = config.overrides;
    this[STORE_TRIAL_LINKS] = config.trialLinks;
    this.cache = new Cache({
      provider: this.getProvider(config.provider)
    });
    this[STORE_FORMATTER] = config.formatter || formatPrice;
  }

  async getProduct(param: ProductSelector): Promise<Product | undefined>;
  async getProduct(param: ProductSelector[]): Promise<(Product | undefined)[]>;
  async getProduct(param: ProductSelector | ProductSelector[]): Promise<undefined | Product | (Product | undefined)[]> {
    const productListToFetch = Array.isArray(param) ? param : [param];
    const productListToReturn = productListToFetch.map(product => this.cache.get(product));

    const products = (await Promise.allSettled(productListToReturn))
      .filter((promise) : promise is PromiseFulfilledResult<Product> => promise.status === "fulfilled" && !!promise.value)
      .map(promise => promise.value);

    if (Array.isArray(param)) {
      return products;
    } else {
      return products.pop();
    }
  }

  private getProvider(param: ProviderSelector): Provider {
    // If provider is a string, use the corresponding built‑in constructor.
    const { name: provider } = param ?? {};

    if (typeof provider === "string") {
      const ctor = BuiltInProviders[provider];
      if (ctor) {
        return new ctor({ store: this });
      }
      throw new Error(`Unknown provider string: ${provider}`);
    }

    // Otherwise, assume provider is a constructor (a class definition).
    // This must be a concrete subclass of the abstract Provider.
    return new provider({ store: this })
  }

  formatPrice(opt: PriceFormat): string | number {
    return this[STORE_FORMATTER](opt);
  }
}
